import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { endpoints } from '../api/endpoints';
import { ErrorBox, durationSeconds, formatDate, formatDuration, formatScore, Loading, useAsync } from '../components/common';

export function SessionsPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const sessions = useAsync(() => endpoints.sessions.list({ limit: 100 }), []);
  const scenarios = useAsync(() => endpoints.scenarios.list().catch(() => []), []);
  const voices = useAsync(() => endpoints.voiceProfiles.list().catch(() => []), []);
  const [scenarioId, setScenarioId] = useState('');
  const [voiceId, setVoiceId] = useState('');
  const [error, setError] = useState<unknown>(null);

  const start = async () => {
    setError(null);
    try {
      const created = await endpoints.sessions.create(scenarioId || null, voiceId || null);
      navigate(`/live/${created.session_id}`, { state: { websocketUrl: created.websocket_url } });
    } catch (err) {
      setError(err);
    }
  };

  return (
    <div>
      <h1>{t('sessions.title')}</h1>
      <section className="card form grid-2" aria-label={t('sessions.new')}>
        <h2 className="span-2">{t('sessions.new')}</h2>
        <label>
          {t('sessions.scenario')}
          <select value={scenarioId} onChange={(e) => setScenarioId(e.target.value)}>
            <option value="">{t('sessions.freeMode')}</option>
            {(scenarios.data ?? []).map((s) => (
              <option key={s.id} value={s.id}>
                {s.title}
              </option>
            ))}
          </select>
        </label>
        <label>
          {t('sessions.voiceProfile')}
          <select value={voiceId} onChange={(e) => setVoiceId(e.target.value)}>
            <option value="">—</option>
            {(voices.data ?? []).map((v) => (
              <option key={v.id} value={v.id}>
                {v.name}
              </option>
            ))}
          </select>
        </label>
        <ErrorBox error={error} />
        <div className="actions span-2">
          <button type="button" onClick={() => void start()}>
            {t('sessions.start')}
          </button>
        </div>
      </section>
      <ErrorBox error={sessions.error} onRetry={sessions.reload} />
      {sessions.loading && <Loading />}
      {!sessions.loading && !sessions.data?.length && <p className="muted">{t('sessions.empty')}</p>}
      {Boolean(sessions.data?.length) && (
        <table>
          <thead>
            <tr>
              <th scope="col">{t('sessions.started')}</th>
              <th scope="col">{t('sessions.duration')}</th>
              <th scope="col">{t('scenarios.statusField')}</th>
              <th scope="col">{t('sessions.score')}</th>
              <th scope="col">{t('common.open')}</th>
            </tr>
          </thead>
          <tbody>
            {sessions.data?.map((s) => (
              <tr key={s.id}>
                <td>{formatDate(s.started_at, i18n.language)}</td>
                <td>{formatDuration(durationSeconds(s.started_at, s.finished_at))}</td>
                <td>{t(`sessions.status.${s.status}`, { defaultValue: s.status })}</td>
                <td>{formatScore(s.total_score)}</td>
                <td>
                  <Link to={`/sessions/${s.id}`}>{t('sessions.details')}</Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

export function SessionDetailPage() {
  const { id } = useParams();
  const { t, i18n } = useTranslation();
  const session = useAsync(() => endpoints.sessions.get(id as string), [id]);
  const s = session.data;

  return (
    <div>
      <h1>{t('sessions.details')}</h1>
      <ErrorBox error={session.error} onRetry={session.reload} />
      {session.loading && <Loading />}
      {s && (
        <>
          <dl className="card details-grid">
            <dt>{t('scenarios.statusField')}</dt>
            <dd>{t(`sessions.status.${s.status}`, { defaultValue: s.status })}</dd>
            <dt>{t('sessions.started')}</dt>
            <dd>{formatDate(s.started_at, i18n.language)}</dd>
            <dt>{t('sessions.finished')}</dt>
            <dd>{formatDate(s.finished_at, i18n.language)}</dd>
            <dt>{t('sessions.score')}</dt>
            <dd>{formatScore(s.total_score)}</dd>
          </dl>
          <h2>{t('sessions.steps')}</h2>
          <table>
            <thead>
              <tr>
                <th scope="col">#</th>
                <th scope="col">{t('sessions.userInput')}</th>
                <th scope="col">{t('sessions.aiResponse')}</th>
                <th scope="col">{t('sessions.accuracy')}</th>
                <th scope="col">{t('sessions.fluency')}</th>
                <th scope="col">{t('sessions.processingTime')}</th>
                <th scope="col">{t('sessions.compare')}</th>
              </tr>
            </thead>
            <tbody>
              {(s.details ?? []).map((d) => (
                <tr key={d.id ?? d.step_order}>
                  <td>{d.step_order}</td>
                  <td>
                    {d.user_input_text}
                    {d.user_audio_url && <audio controls preload="none" src={d.user_audio_url} aria-label={t('sessions.userInput')} />}
                  </td>
                  <td>
                    {d.ai_response_text}
                    {d.ai_response_audio_url && (
                      <audio controls preload="none" src={d.ai_response_audio_url} aria-label={t('sessions.aiResponse')} />
                    )}
                  </td>
                  <td>{formatScore(d.recognition_accuracy)}</td>
                  <td>{formatScore(d.fluency_score)}</td>
                  <td>{d.processing_time_ms ?? '—'}</td>
                  <td>
                    {d.user_audio_url && d.ai_response_audio_url ? (
                      <Link to={`/compare?session=${s.id}&step=${d.step_order}`}>{t('sessions.compare')}</Link>
                    ) : (
                      '—'
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
}
